import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";

export const DrawingSelector = ({
  projectId = null,
  selected = null,
  onChange,
  placeholder = "Velg tegning...",
  disabled = false,
  zIndex,
}) => {
  const { t } = useTranslation();
  const [drawings, setDrawings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useAutofocusOnOpen(isOpen);

  useEffect(() => {
    if (isOpen) {
      loadDrawings();
    }
  }, [isOpen, projectId]);

  const loadDrawings = async () => {
    setIsLoading(true);
    try {
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
        fields: "id,title,description,document.file",
      };

      if (projectId) {
        params.project_id = projectId;
      }

      const response = await axios({
        method: "get",
        url: "/drawings/",
        params: params,
      });

      setDrawings(response.data.data || []);
    } catch (error) {
      console.error("Error loading drawings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && searchQuery) {
      const timer = setTimeout(() => {
        loadDrawings();
      }, 300);
      return () => clearTimeout(timer);
    } else if (isOpen) {
      loadDrawings();
    }
  }, [searchQuery, isOpen]);

  const handleSelect = (drawing) => {
    onChange(drawing);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className={selected ? "text-gray-900" : "text-gray-500"}>
          {selected ? selected.title : placeholder}
        </span>
        {selected && !disabled ? (
          <button
            type="button"
            onClick={handleClear}
            className="ml-2 text-gray-400 hover:text-gray-600"
            aria-label={t("Clear")}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </button>

      <PMDialog
        open={isOpen}
        onClose={handleClose}
        title={t("documentUpload.preview.drawingDialogTitle")}
        size="md"
        dense
        closeOnOutsideClick
        zIndex={zIndex}
      >
        <div className="flex max-h-[min(60vh,24rem)] min-h-0 flex-col">
          <div className="shrink-0 border-b border-gray-200 p-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("documentUpload.preview.drawingSearchPlaceholder")}
                className="w-full rounded border border-gray-300 py-1 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <SelectorListSkeleton rows={4} />
            ) : drawings.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {t("documentUpload.preview.drawingEmpty")}
              </div>
            ) : (
              <div className="py-1">
                {drawings.map((drawing) => (
                  <button
                    key={drawing.id}
                    type="button"
                    onClick={() => handleSelect(drawing)}
                    className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {drawing.title}
                    </div>
                    {drawing.description ? (
                      <div className="truncate text-xs text-gray-500">
                        {drawing.description}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </PMDialog>
    </>
  );
};
